import Cancel from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ValidationErrors } from 'fluentvalidation-ts';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CustomButton from '../../../../Common/Components/Controls/InputControls';
import { Loader } from '../../../../Common/Components/Loader';
import {
  FooterContainer,
  FooterWrapper,
  PaperWrapper,
} from '../../Common/CommonComponents';
import { showToast } from '../../../../lib/toast';

import { customerService } from '../CustomerService';
import { InputClaimTypes, NewCustomer } from '../Model/CustomerModel';
import { EnvOption, EnvironmentOptionDto } from '../Model/CustomerProps';
import CustomerForm from './CustomerForm';

interface Param {
  customerId: string;
}

export default function EditCustomerPage() {
  const { t } = useTranslation('customer');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { customerId } = useParams<Param>();
  const history = useHistory();

  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [envOptions, setEnvOptions] = useState<EnvOption[]>([]);
  const [customer, setCustomer] = useState<NewCustomer>({});
  const [loading, setLoading] = useState(0);
  const [customerErrors, setCustomerErrors] = useState<
    ValidationErrors<NewCustomer>
  >({});

  useEffect(() => {
    initData();
  }, []);

  /* -------------------- Data Loading -------------------- */

  const initData = async () => {
    await Promise.all([loadGroupNames(), loadEnvOptions(), loadCustomer()]);
  };

  const loadGroupNames = async () => {
    try {
      setLoading((c) => c + 1);
      const res = await customerService.getGroupNames();
      setGroupNames(res.Items);
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  const loadEnvOptions = async () => {
    try {
      setLoading((c) => c + 1);
      const res = await customerService.getEnvironmentOptions();
      setEnvOptions(
        res.map((dto: EnvironmentOptionDto) => ({
          value: dto.Value,
          name: dto.Name,
        })),
      );
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  const loadCustomer = async () => {
    try {
      setLoading((c) => c + 1);
      const existing = await customerService.getEditCustomer(customerId);
      setCustomer((prev) => ({ ...prev, ...existing }));
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  /* -------------------- Handlers -------------------- */

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      setCustomer((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'IdentityProviders') {
      setCustomer((prev) => ({
        ...prev,
        IdentityProviders: value,
        InputClaimType: InputClaimTypes[value],
      }));
    } else {
      setCustomer((prev) => ({ ...prev, [name]: value }));
    }
  };

  const updateAdminUserPassword = (password: string) => {
    setCustomer((prev) => ({ ...prev, AdminUserPassword: password }));
  };

  /* -------------------- Validation -------------------- */

  const validateAuthenticationTypes = (): boolean => {
    if (!customer.EnableProArcLogin && !customer.ExternalIdentityProvider) {
      showToast({ title: t('validation.auth_type'), type: 'error' });
      return false;
    }
    return true;
  };

  const validateAuthenticationSection = (): boolean => {
    if (
      customer.ExternalIdentityProvider &&
      (customerErrors.AzureADAppClientID ||
        customerErrors.WSFederationUrl ||
        customerErrors.IdentityProviders ||
        customerErrors.InputClaimType ||
        customerErrors.AuthProviderName)
    ) {
      showToast({ title: t('validation.common_field'), type: 'error' });
      return false;
    }
    return true;
  };

  const validateBasicDetailsSection = (): boolean => {
    if (
      customerErrors.Subdomain ||
      customerErrors.CustomerName ||
      customerErrors.ContactPersons ||
      customerErrors.GroupName ||
      customerErrors.AdminUserName ||
      customerErrors.AdminUser ||
      customerErrors.AdminUserPassword
    ) {
      showToast({ title: t('validation.common_field'), type: 'error' });
      return false;
    }
    return true;
  };

  /* -------------------- Save -------------------- */

  const updateCustomer = async () => {
    try {
      setLoading((c) => c + 1);
      const updated = await customerService.updateCustomer(customer);
      if (updated) {
        showToast({ title: t('update_success'), type: 'success' });
        history.push('/customer');
      }
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  const handleSave = () => {
    if (
      validateAuthenticationTypes() &&
      validateAuthenticationSection() &&
      validateBasicDetailsSection()
    ) {
      updateCustomer();
    }
  };

  /* -------------------- Render -------------------- */

  return (
    <PaperWrapper>
      <Loader loading={loading > 0} />

      <Typography variant="h5" sx={{ mb: 2 }}>
        Edit Customer: {customer.CustomerName}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <CustomerForm
        groupNames={groupNames}
        envOptions={envOptions}
        onFieldChange={handleChange}
        customer={customer}
        errors={customerErrors}
        setErrors={setCustomerErrors}
        ops="Edit"
        updateUserAdminPassword={updateAdminUserPassword}
      />

      <FooterWrapper>
        <FooterContainer>
          <Grid item xs={12} sm="auto">
            <CustomButton
              variant="contained"
              onClick={handleSave}
              fullWidth={isMobile}
              startIcon={<SaveIcon />}
              label="Save"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <CustomButton
              variant="contained"
              onClick={() => history.push('/customer')}
              fullWidth={isMobile}
              startIcon={<Cancel />}
              label="Cancel"
            />
          </Grid>
        </FooterContainer>
      </FooterWrapper>
    </PaperWrapper>
  );
}
